import { useEffect, useState } from 'react';
import notificationService from '../domain/services/notification/customer.comment.notification.service';
import loginService from '../domain/services/login.service';

function useViewedRequestComments(historyRef, comments) {
    const [readError, setReadError] = useState(false);

    useEffect(() => {
        setReadError(false);
        if (!loginService.isCustomer() || !historyRef.current || !comments.length ||
            typeof IntersectionObserver !== 'function') return;

        let active = true;
        let reading = false;
        const visible = new Set();
        const confirmed = new Set();
        const confirmRead = async () => {
            if (!active || reading || document.visibilityState !== 'visible') return;
            const ids = [...visible].filter(id => !confirmed.has(id));
            if (!ids.length) return;

            reading = true;
            let succeeded = false;
            try {
                await notificationService.readComments(ids);
                if (!active) return;
                ids.forEach(id => confirmed.add(id));
                setReadError(false);
                succeeded = true;
            } catch (error) {
                if (active) setReadError(true);
            } finally {
                reading = false;
            }
            if (succeeded) confirmRead();
        };

        const observer = new IntersectionObserver(entries => {
            for (const entry of entries) {
                const id = entry.target.dataset.commentId;
                if (entry.isIntersecting && entry.intersectionRatio > 0) visible.add(id);
                else visible.delete(id);
            }
            confirmRead();
        });
        historyRef.current.querySelectorAll('[data-comment-id]').forEach(element =>
            observer.observe(element)
        );
        document.addEventListener('visibilitychange', confirmRead);
        window.addEventListener('focus', confirmRead);
        return () => {
            active = false;
            observer.disconnect();
            document.removeEventListener('visibilitychange', confirmRead);
            window.removeEventListener('focus', confirmRead);
        };
    }, [historyRef, comments]);

    return readError;
}

export default useViewedRequestComments;
